import React from "react";
import {
    Box,
    Button,
    Checkbox,
    Drawer,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Close, Delete } from "@mui/icons-material";
import type { Edge } from "@xyflow/react";
import type {
    EvaluationDomain,
    ResolverActionType,
    ResolverAttributeEntry,
    WhenDto,
} from "../../../../types/resolver";
import type {
    ResolverEdgeData,
    ResolverFlowNode,
    ResolverGraphNodeData,
    ResolverCodeType,
} from "./utils";
import { getDefaultActionDetails } from "./utils";
import { WhenBuilder, WhenVisualizer } from "./WhenBuilder";
import { DerivedAttributeInput } from "./DerivedAttributeInput";
import { DerivedAttributeValueInput } from "./DerivedAttributeValueInput";
import { QuestionSearchAttributeAdder } from "./QuestionSearchAttributeAdder";
import { SetAnswerFields } from "./SetAnswerFields";

export interface NodeEdgeDetailsDrawerProps {
    open: boolean;
    selectedNode: ResolverFlowNode | null | undefined;
    selectedEdge: Edge<ResolverEdgeData> | null | undefined;
    nodesCount: number;
    declaredDerivedAttributeNames: string[];
    edgeDrawerEvaluationDomain: EvaluationDomain | undefined;
    existingValuesByDerivedAttributeName: Record<string, unknown[]>;
    evaluationDomains: EvaluationDomain[];
    actionTypes: ResolverActionType[];
    codeTypeOptions: ResolverCodeType[];
    onClose: () => void;
    onUpdateNodeData: (patch: Partial<ResolverGraphNodeData>) => void;
    onUpdateNodeAction: (
        actionId: string,
        patch: {
            action?: ResolverActionType;
            actionDetails?: Record<string, unknown>;
            rules?: { when?: WhenDto } | null;
        }
    ) => void;
    onAddAction: () => void;
    onRemoveAction: (actionId: string) => void;
    onUpdateEdgeData: (patch: Partial<ResolverEdgeData>) => void;
    onDeleteNode: () => void;
    onDeleteEdge: () => void;
    onUpdateCodeActionAttributeEntry: (
        actionId: string,
        attributeId: string,
        patch: Partial<ResolverAttributeEntry>
    ) => void;
    onRemoveCodeActionAttributeEntry: (actionId: string, attributeId: string) => void;
    onAddCodeActionAttributes: (actionId: string, entry: ResolverAttributeEntry) => void;
    onUpdateCodeActionDerivedAttributes: (
        actionId: string,
        derivedAttributes: Record<string, unknown>
    ) => void;
}

export function NodeEdgeDetailsDrawer({
    open,
    selectedNode,
    selectedEdge,
    nodesCount,
    declaredDerivedAttributeNames,
    edgeDrawerEvaluationDomain,
    existingValuesByDerivedAttributeName,
    evaluationDomains,
    actionTypes,
    codeTypeOptions,
    onClose,
    onUpdateNodeData,
    onUpdateNodeAction,
    onAddAction,
    onRemoveAction,
    onUpdateEdgeData,
    onDeleteNode,
    onDeleteEdge,
    onUpdateCodeActionAttributeEntry,
    onRemoveCodeActionAttributeEntry,
    onAddCodeActionAttributes,
    onUpdateCodeActionDerivedAttributes,
}: NodeEdgeDetailsDrawerProps) {
    const actionsList = selectedNode?.data?.actions ?? [];

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: 420, p: 2 } }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6">
                    {selectedEdge ? "Edge Details" : "Node Details"}
                </Typography>
                <Stack direction="row" spacing={0.5}>
                    {selectedNode && (
                        <IconButton
                            aria-label="delete node"
                            onClick={onDeleteNode}
                            disabled={nodesCount <= 1}
                            color="error"
                            size="small"
                            title={nodesCount <= 1 ? "Cannot delete the last node" : "Delete node"}
                        >
                            <Delete />
                        </IconButton>
                    )}
                    {selectedEdge && (
                        <IconButton
                            aria-label="delete edge"
                            onClick={onDeleteEdge}
                            color="error"
                            size="small"
                            title="Delete edge"
                        >
                            <Delete />
                        </IconButton>
                    )}
                    <IconButton aria-label="close drawer" onClick={onClose}>
                        <Close />
                    </IconButton>
                </Stack>
            </Stack>
            {selectedEdge && (
                <Stack spacing={1.5}>
                    <Typography variant="body2" color="text.secondary">
                        Edge from {selectedEdge.source} → {selectedEdge.target}. Add an optional
                        condition to take this edge.
                    </Typography>
                    <Typography variant="subtitle2">When (optional)</Typography>
                    <WhenVisualizer when={selectedEdge.data?.when} />
                    <WhenBuilder
                        value={selectedEdge.data?.when}
                        onChange={(when) => onUpdateEdgeData({ when: when ?? undefined })}
                        noRemove
                        evaluationDomain={edgeDrawerEvaluationDomain}
                        declaredDerivedAttributeNames={declaredDerivedAttributeNames}
                    />
                </Stack>
            )}
            {!selectedNode && !selectedEdge && (
                <Typography variant="body2">Select a node or edge to edit.</Typography>
            )}
            {selectedNode && !selectedEdge && (
                <Stack spacing={1.5}>
                    <TextField
                        size="small"
                        label="Label"
                        value={selectedNode.data.label}
                        onChange={(event) => onUpdateNodeData({ label: event.target.value })}
                    />
                    <FormControl size="small">
                        <InputLabel id="resolver-evaluates-label">Evaluates</InputLabel>
                        <Select
                            labelId="resolver-evaluates-label"
                            label="Evaluates"
                            value={selectedNode.data.evaluates}
                            onChange={(event) =>
                                onUpdateNodeData({
                                    evaluates: event.target.value as EvaluationDomain,
                                })
                            }
                        >
                            {evaluationDomains.map((domain) => (
                                <MenuItem key={domain} value={domain}>
                                    {domain}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography variant="subtitle2" sx={{ pt: 0.5 }}>
                        Actions
                    </Typography>
                    {actionsList.map((graphAction) => {
                        const availableActionTypes =
                            selectedNode?.data?.evaluates === "CODES"
                                ? actionTypes
                                : actionTypes.filter((t) => t !== "MARK_CODE_AS_IDEAL");
                        return (
                            <Paper key={graphAction.id} variant="outlined" sx={{ p: 1.25 }}>
                                <Stack spacing={1}>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel id={`action-type-${graphAction.id}`}>
                                                Action type
                                            </InputLabel>
                                            <Select
                                                labelId={`action-type-${graphAction.id}`}
                                                label="Action type"
                                                value={graphAction.action}
                                                onChange={(event) => {
                                                    const newType =
                                                        event.target.value as ResolverActionType;
                                                    onUpdateNodeAction(graphAction.id, {
                                                        action: newType,
                                                        actionDetails: getDefaultActionDetails(
                                                            newType
                                                        ),
                                                    });
                                                }}
                                            >
                                                {availableActionTypes.map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                        {type}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <IconButton
                                            size="small"
                                            aria-label={`Remove action ${graphAction.action}`}
                                            onClick={() => onRemoveAction(graphAction.id)}
                                            color="error"
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                    {graphAction.action === "SET_DERIVED_ATTRIBUTE" && (
                                        <>
                                            <DerivedAttributeInput
                                                value={String(
                                                    graphAction.actionDetails?.derivedAttributeName ??
                                                        "RESULT"
                                                )}
                                                onChange={(name) =>
                                                    onUpdateNodeAction(graphAction.id, {
                                                        actionDetails: {
                                                            ...graphAction.actionDetails,
                                                            derivedAttributeName: name,
                                                        },
                                                    })
                                                }
                                                existingNames={declaredDerivedAttributeNames}
                                                label="Derived attribute name"
                                                placeholder="Select or type name and press Enter"
                                            />
                                            <FormControl size="small" fullWidth>
                                                <InputLabel id={`derived-type-${graphAction.id}`}>
                                                    Value type
                                                </InputLabel>
                                                <Select
                                                    labelId={`derived-type-${graphAction.id}`}
                                                    label="Value type"
                                                    value={String(
                                                        graphAction.actionDetails
                                                            ?.derivedAttributeType ?? "STRING"
                                                    )}
                                                    onChange={(e) =>
                                                        onUpdateNodeAction(graphAction.id, {
                                                            actionDetails: {
                                                                ...graphAction.actionDetails,
                                                                derivedAttributeType: e.target
                                                                    .value as
                                                                    | "BOOLEAN"
                                                                    | "NUMBER"
                                                                    | "STRING",
                                                            },
                                                        })
                                                    }
                                                >
                                                    <MenuItem value="BOOLEAN">BOOLEAN</MenuItem>
                                                    <MenuItem value="NUMBER">NUMBER</MenuItem>
                                                    <MenuItem value="STRING">STRING</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <DerivedAttributeValueInput
                                                value={
                                                    graphAction.actionDetails?.derivedAttributeValue
                                                }
                                                valueType={
                                                    (graphAction.actionDetails
                                                        ?.derivedAttributeType as
                                                        | "BOOLEAN"
                                                        | "NUMBER"
                                                        | "STRING") ?? "STRING"
                                                }
                                                onChange={(val) =>
                                                    onUpdateNodeAction(graphAction.id, {
                                                        actionDetails: {
                                                            ...graphAction.actionDetails,
                                                            derivedAttributeValue: val,
                                                        },
                                                    })
                                                }
                                                existingValues={
                                                    existingValuesByDerivedAttributeName[
                                                        String(
                                                            graphAction.actionDetails?.derivedAttributeName ??
                                                                ""
                                                        ).trim()
                                                    ] ?? []
                                                }
                                                label="Value"
                                                placeholder="Select or type value and press Enter"
                                            />
                                        </>
                                    )}
                                    {graphAction.action === "SET_ANSWER" && (
                                        <SetAnswerFields
                                            questionId={
                                                graphAction.actionDetails?.questionId as
                                                    | string
                                                    | number
                                                    | undefined
                                            }
                                            answerSetId={
                                                graphAction.actionDetails?.answerSetId as
                                                    | string
                                                    | number
                                                    | undefined
                                            }
                                            answerId={
                                                graphAction.actionDetails?.answerId as
                                                    | string
                                                    | number
                                                    | undefined
                                            }
                                            onChange={(details) =>
                                                onUpdateNodeAction(graphAction.id, {
                                                    actionDetails: {
                                                        ...graphAction.actionDetails,
                                                        ...details,
                                                    },
                                                })
                                            }
                                        />
                                    )}
                                    {graphAction.action === "ADD_CODE" && (
                                        <>
                                            <TextField
                                                size="small"
                                                label="Code"
                                                value={String(graphAction.actionDetails?.code ?? "")}
                                                onChange={(e) =>
                                                    onUpdateNodeAction(graphAction.id, {
                                                        actionDetails: {
                                                            ...graphAction.actionDetails,
                                                            code: e.target.value,
                                                        },
                                                    })
                                                }
                                            />
                                            <FormControl size="small">
                                                <InputLabel id={`code-type-${graphAction.id}`}>
                                                    Code type
                                                </InputLabel>
                                                <Select
                                                    labelId={`code-type-${graphAction.id}`}
                                                    label="Code type"
                                                    value={String(
                                                        graphAction.actionDetails?.codeType ??
                                                            "DIAGNOSIS"
                                                    )}
                                                    onChange={(e) =>
                                                        onUpdateNodeAction(graphAction.id, {
                                                            actionDetails: {
                                                                ...graphAction.actionDetails,
                                                                codeType: e.target
                                                                    .value as ResolverCodeType,
                                                            },
                                                        })
                                                    }
                                                >
                                                    {codeTypeOptions.map((t) => (
                                                        <MenuItem key={t} value={t}>
                                                            {t}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={Boolean(
                                                            graphAction.actionDetails?.mark_code_as_ideal
                                                        )}
                                                        onChange={(e) =>
                                                            onUpdateNodeAction(graphAction.id, {
                                                                actionDetails: {
                                                                    ...graphAction.actionDetails,
                                                                    mark_code_as_ideal:
                                                                        e.target.checked,
                                                                },
                                                            })
                                                        }
                                                    />
                                                }
                                                label="Mark as ideal"
                                            />
                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                            >
                                                <Typography variant="caption">
                                                    Attributes (Question/Answer)
                                                </Typography>
                                            </Stack>
                                            {(
                                                graphAction.actionDetails?.attributes as
                                                    | ResolverAttributeEntry[]
                                                    | undefined
                                            )?.map((attribute) => (
                                                <Box
                                                    key={attribute.id}
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {attribute.questionLabel}
                                                    </Typography>
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        alignItems="center"
                                                    >
                                                        {attribute.answerOptions &&
                                                        attribute.answerOptions.length > 0 ? (
                                                            <FormControl
                                                                size="small"
                                                                sx={{ minWidth: 160, flex: 1 }}
                                                            >
                                                                <InputLabel
                                                                    id={`addcode-attr-${attribute.id}-label`}
                                                                >
                                                                    Answer
                                                                </InputLabel>
                                                                <Select
                                                                    labelId={`addcode-attr-${attribute.id}-label`}
                                                                    label="Answer"
                                                                    value={
                                                                        attribute.answerValueId ?? ""
                                                                    }
                                                                    onChange={(e) =>
                                                                        onUpdateCodeActionAttributeEntry(
                                                                            graphAction.id,
                                                                            attribute.id,
                                                                            {
                                                                                answerValueId: Number(
                                                                                    e.target.value
                                                                                ),
                                                                            }
                                                                        )
                                                                    }
                                                                >
                                                                    {attribute.answerOptions.map(
                                                                        (opt) => (
                                                                            <MenuItem
                                                                                key={opt.id}
                                                                                value={opt.id}
                                                                            >
                                                                                {opt.displayText}
                                                                            </MenuItem>
                                                                        )
                                                                    )}
                                                                </Select>
                                                            </FormControl>
                                                        ) : (
                                                            <TextField
                                                                size="small"
                                                                label="Answer"
                                                                value={
                                                                    attribute.answerDisplayText ?? ""
                                                                }
                                                                onChange={(e) =>
                                                                    onUpdateCodeActionAttributeEntry(
                                                                        graphAction.id,
                                                                        attribute.id,
                                                                        {
                                                                            answerDisplayText:
                                                                                e.target.value,
                                                                        }
                                                                    )
                                                                }
                                                                fullWidth
                                                                sx={{ flex: 1 }}
                                                            />
                                                        )}
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            onClick={() =>
                                                                onRemoveCodeActionAttributeEntry(
                                                                    graphAction.id,
                                                                    attribute.id
                                                                )
                                                            }
                                                            aria-label={`Remove attribute ${attribute.questionLabel}`}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Stack>
                                                </Box>
                                            ))}
                                            <QuestionSearchAttributeAdder
                                                onAdd={(entry) =>
                                                    onAddCodeActionAttributes(
                                                        graphAction.id,
                                                        entry
                                                    )
                                                }
                                                existingQuestionIds={
                                                    (
                                                        graphAction.actionDetails?.attributes as
                                                            | ResolverAttributeEntry[]
                                                            | undefined
                                                    )?.map((a) => a.questionId) ?? []
                                                }
                                                label="Add attribute"
                                            />
                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                sx={{ mt: 1 }}
                                            >
                                                <Typography variant="caption">
                                                    Derived attributes
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => {
                                                        const current =
                                                            (graphAction.actionDetails
                                                                ?.derivedAttributes as Record<
                                                                string,
                                                                unknown
                                                            >) ?? {};
                                                        const key = `attr_${Date.now()}`;
                                                        onUpdateCodeActionDerivedAttributes(
                                                            graphAction.id,
                                                            {
                                                                ...current,
                                                                [key]: "",
                                                            }
                                                        );
                                                    }}
                                                >
                                                    Add derived attribute
                                                </Button>
                                            </Stack>
                                            {Object.entries(
                                                (graphAction.actionDetails?.derivedAttributes as
                                                    Record<string, unknown>) ?? {}
                                            ).map(([key, value]) => (
                                                <Stack
                                                    key={key}
                                                    direction="row"
                                                    spacing={1}
                                                >
                                                    <DerivedAttributeInput
                                                        value={key}
                                                        onChange={(newKey) => {
                                                            const derived =
                                                                (graphAction.actionDetails
                                                                    ?.derivedAttributes as Record<
                                                                    string,
                                                                    unknown
                                                                >) ?? {};
                                                            const next = { ...derived };
                                                            delete next[key];
                                                            if (newKey.trim())
                                                                next[newKey.trim()] = value;
                                                            onUpdateCodeActionDerivedAttributes(
                                                                graphAction.id,
                                                                next
                                                            );
                                                        }}
                                                        existingNames={[
                                                            ...declaredDerivedAttributeNames,
                                                            ...Object.keys(
                                                                (graphAction.actionDetails
                                                                    ?.derivedAttributes as Record<
                                                                    string,
                                                                    unknown
                                                                >) ?? {}
                                                            ).filter((k) => k !== key),
                                                        ]}
                                                        label="Name"
                                                        placeholder="e.g. IS_PRIMARY"
                                                        commitOnly
                                                        fullWidth
                                                        sx={{ flex: 1 }}
                                                    />
                                                    <DerivedAttributeValueInput
                                                        value={value}
                                                        onChange={(val) => {
                                                            const derived =
                                                                (graphAction.actionDetails
                                                                    ?.derivedAttributes as Record<
                                                                    string,
                                                                    unknown
                                                                >) ?? {};
                                                            onUpdateCodeActionDerivedAttributes(
                                                                graphAction.id,
                                                                {
                                                                    ...derived,
                                                                    [key]: val,
                                                                }
                                                            );
                                                        }}
                                                        existingValues={
                                                            existingValuesByDerivedAttributeName[
                                                                key
                                                            ] ?? []
                                                        }
                                                        label="Value"
                                                        placeholder="Select or type value and press Enter"
                                                        sx={{ flex: 1 }}
                                                    />
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        onClick={() => {
                                                            const derived =
                                                                (graphAction.actionDetails
                                                                    ?.derivedAttributes as Record<
                                                                    string,
                                                                    unknown
                                                                >) ?? {};
                                                            const next = { ...derived };
                                                            delete next[key];
                                                            onUpdateCodeActionDerivedAttributes(
                                                                graphAction.id,
                                                                next
                                                            );
                                                        }}
                                                        aria-label={`Remove derived attribute ${key}`}
                                                    >
                                                        Remove
                                                    </Button>
                                                </Stack>
                                            ))}
                                        </>
                                    )}
                                    {graphAction.action === "THROW_ERROR" && (
                                        <>
                                            <TextField
                                                size="small"
                                                label="Message (required)"
                                                value={String(
                                                    graphAction.actionDetails?.message ?? ""
                                                )}
                                                onChange={(e) =>
                                                    onUpdateNodeAction(graphAction.id, {
                                                        actionDetails: {
                                                            ...graphAction.actionDetails,
                                                            message: e.target.value,
                                                        },
                                                    })
                                                }
                                            />
                                            <TextField
                                                size="small"
                                                label="Error code"
                                                value={String(
                                                    graphAction.actionDetails?.errorCode ?? ""
                                                )}
                                                onChange={(e) =>
                                                    onUpdateNodeAction(graphAction.id, {
                                                        actionDetails: {
                                                            ...graphAction.actionDetails,
                                                            errorCode: e.target.value,
                                                        },
                                                    })
                                                }
                                            />
                                            <TextField
                                                size="small"
                                                label="Severity"
                                                value={String(
                                                    graphAction.actionDetails?.severity ?? "ERROR"
                                                )}
                                                onChange={(e) =>
                                                    onUpdateNodeAction(graphAction.id, {
                                                        actionDetails: {
                                                            ...graphAction.actionDetails,
                                                            severity: e.target.value,
                                                        },
                                                    })
                                                }
                                            />
                                            <TextField
                                                size="small"
                                                label="Field"
                                                value={String(
                                                    graphAction.actionDetails?.field ?? ""
                                                )}
                                                onChange={(e) =>
                                                    onUpdateNodeAction(graphAction.id, {
                                                        actionDetails: {
                                                            ...graphAction.actionDetails,
                                                            field: e.target.value,
                                                        },
                                                    })
                                                }
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={
                                                            graphAction.actionDetails
                                                                ?.stopExecution !== false
                                                        }
                                                        onChange={(e) =>
                                                            onUpdateNodeAction(graphAction.id, {
                                                                actionDetails: {
                                                                    ...graphAction.actionDetails,
                                                                    stopExecution: e.target.checked,
                                                                },
                                                            })
                                                        }
                                                    />
                                                }
                                                label="Stop execution"
                                            />
                                        </>
                                    )}
                                    {graphAction.action === "MARK_CODE_AS_IDEAL" && (
                                        <Typography variant="body2" color="text.secondary">
                                            Marks the current code in scope as ideal. Only valid on
                                            nodes that evaluate CODES.
                                        </Typography>
                                    )}
                                    <Typography variant="subtitle2" sx={{ pt: 0.5 }}>
                                        When (optional)
                                    </Typography>
                                    <WhenVisualizer when={graphAction.rules?.when} />
                                    <WhenBuilder
                                        value={graphAction.rules?.when}
                                        onChange={(when) =>
                                            onUpdateNodeAction(graphAction.id, {
                                                rules: when ? { when } : null,
                                            })
                                        }
                                        noRemove
                                        evaluationDomain={selectedNode?.data?.evaluates}
                                        declaredDerivedAttributeNames={
                                            declaredDerivedAttributeNames
                                        }
                                    />
                                </Stack>
                            </Paper>
                        );
                    })}
                    <Button variant="outlined" size="small" onClick={onAddAction}>
                        Add action
                    </Button>
                </Stack>
            )}
        </Drawer>
    );
}
